/**
 * DrManiEmpathyScreen - motivational interstitial between quiz sections.
 * Tap anywhere (or wait for the timer) to continue — mirrors USCN EmpathyScreen.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import DrManiProgressTimer from './DrManiProgressTimer';
import { DMM_TRANSITION_DURATION_MS } from '../../data/drManiQuiz/interstitials';

interface DrManiEmpathyScreenProps {
  screenId: string;
  headline: string;
  description: string;
  onContinue: () => void;
  onBack?: (() => void) | undefined;
}

const DrManiEmpathyScreen: React.FC<DrManiEmpathyScreenProps> = ({
  screenId,
  headline,
  description,
  onContinue,
  onBack,
}) => {
  const hasContinuedRef = useRef(false);

  useEffect(() => {
    hasContinuedRef.current = false;
  }, [screenId]);

  const continueOnce = useCallback(() => {
    if (hasContinuedRef.current) return;
    hasContinuedRef.current = true;
    onContinue();
  }, [onContinue]);

  const handleTapKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      continueOnce();
    }
  };

  return (
    <div
      className="dmm-empathy-screen dmm-empathy-screen-tappable"
      onClick={continueOnce}
      onKeyDown={handleTapKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="dmm-empathy-screen-content">
        <h1 className="dmm-empathy-headline">{headline}</h1>
        {description && <p className="dmm-empathy-body">{description}</p>}
      </div>
      <DrManiProgressTimer duration={DMM_TRANSITION_DURATION_MS} resetKey={screenId} onComplete={continueOnce} />
      {onBack && (
        <div
          className="dmm-slide-navigation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <button type="button" className="dmm-btn dmm-btn-back" onClick={onBack}>
            ← Back
          </button>
        </div>
      )}
    </div>
  );
};

export default DrManiEmpathyScreen;
